/**
 * Incident Kanban Card Component
 * Draggable card for displaying incident in Kanban view
 *
 * @component
 * @description Individual incident card in Kanban board. Uses shared Kanban primitives for drag-and-drop
 * and keeps the UI compact for the new board layout.
 *
 * @example
 * ```tsx
 * <IncidentKanbanCard
 *   incident={incident}
 *   onClick={(inc) => openWindow(inc)}
 * />
 * ```
 */

import { memo } from 'react';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Clock, Building2 } from 'lucide-react';
import type { Incident } from '@/types/incidents';
import { getSeverityLabel, getSeverityColor } from '@/lib/incidentHelpers';
import { cn } from '@/lib/utils';
import { KanbanCard } from '@/components/ui/shadcn-io/kanban';
import { mapLegacyStateToStatusReason } from './kanban-config';

interface IncidentKanbanCardProps {
  incident: Incident;
  onClick: (incident: Incident) => void;
}

/**
 * Memoized component to prevent unnecessary re-renders when other cards update.
 * Only re-renders when incident data or onClick function changes.
 */
export const IncidentKanbanCard = memo(function IncidentKanbanCard({ incident, onClick }: IncidentKanbanCardProps) {
  return (
    <KanbanCard
      id={incident.id}
      name={incident.title || incident.incidentNumber}
      column={incident.statusReason || mapLegacyStateToStatusReason(incident.state || 'new')}
      className="cursor-pointer transition-all hover:shadow-md"
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => onClick(incident)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick(incident);
          }
        }}
        aria-label={`Incidente ${incident.incidentNumber}: ${incident.title}. Click para abrir en ventana flotante.`}
        className="flex flex-col gap-3 focus:outline-none"
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 flex-1 flex-col">
              <CardTitle className="truncate text-sm font-semibold">
                {incident.incidentNumber}
              </CardTitle>
              <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                {incident.title}
              </p>
            </div>
            <Badge className={cn('whitespace-nowrap', getSeverityColor(incident.severity))}>
              {getSeverityLabel(incident.severity)}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-3 pt-0">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Building2 className="h-3 w-3 shrink-0" />
            <span className="truncate capitalize">{incident.type}</span>
          </div>

          {incident.assignedTo && (
            <div className="flex items-center gap-2">
              <Avatar className="h-5 w-5">
                <AvatarFallback className="text-xs">
                  {incident.assignedTo.fullName
                    ?.split(' ')
                    .map((namePart) => namePart[0])
                    .join('')
                    .toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="truncate text-xs text-muted-foreground">
                {incident.assignedTo.fullName}
              </span>
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-3 w-3 shrink-0" />
            <span>
              {new Date(incident.createdAt).toLocaleDateString('es-CL', {
                month: 'short',
                day: 'numeric',
              })}
            </span>
          </div>
        </CardContent>
      </div>
    </KanbanCard>
  );
});
